// Create dashboard from this device and Add to dashboard (WEB-DSH-DEV, DDB-28), on the device page's grid.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../ui/Button";
import type { Widget } from "../../widgets/types";
import { appendWidget, asDocument, fromDevicePage, MAX_WIDGETS } from "./copy";
import { saveErrors } from "./errors";
import { createDashboard, dashboardQuery, dashboardsQuery, saveDashboard } from "./queries";

/** Copies the device page's grid into a new private dashboard named after the device, then opens it. */
export function CreateFromDevice({ orgId, name, widgets }: { orgId: string; name: string; widgets: Widget[] }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const create = useMutation({
    mutationFn: () => createDashboard(fromDevicePage(name, widgets)),
    onSuccess: (d) => {
      void qc.invalidateQueries({ queryKey: dashboardsQuery(orgId).queryKey });
      void navigate({ to: "/dashboards/$dashboardId", params: { dashboardId: d.id } });
    },
  });
  return (
    <div>
      <Button variant="secondary" disabled={create.isPending} onClick={() => create.mutate()}>
        {t("dashboards.fromDevice")}
      </Button>
      {create.isError && (
        <ul role="alert" className="mt-2 text-sm text-red-900">
          {saveErrors(create.error, t, (i) => widgets[i]?.title).map((e, i) => (
            <li key={i}>{e}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Add to dashboard under one widget: the dashboards the user may edit with room left, or a new private one named
 * after the device. The widget goes below the dashboard's last one.
 */
export function AddToDashboard({ orgId, widget, deviceName }: { orgId: string; widget: Widget; deviceName: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const listId = useId();
  const [open, setOpen] = useState(false);
  const list = useQuery({ ...dashboardsQuery(orgId), enabled: open });
  const add = useMutation({
    mutationFn: async (target: { id: string } | "new") => {
      if (target === "new") {
        const doc = appendWidget(fromDevicePage(deviceName, []), widget);
        if (!doc) throw new Error("full");
        return createDashboard(doc);
      }
      const current = await qc.fetchQuery({ ...dashboardQuery(orgId, target.id), staleTime: 0 });
      const doc = asDocument(current.document);
      const next = doc && appendWidget(doc, widget);
      if (!next) throw new Error("full");
      return saveDashboard(target.id, next, current.shared, current.etag);
    },
    onSuccess: (d) => {
      setOpen(false);
      void qc.invalidateQueries({ queryKey: dashboardsQuery(orgId).queryKey });
      void qc.invalidateQueries({ queryKey: dashboardQuery(orgId, d.id).queryKey });
    },
  });
  const editable = (list.data ?? []).filter((d) => d.can_edit);
  const label = widget.title ?? t("dashboards.thisWidget");
  return (
    <div className="text-xs">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={listId}
        aria-label={t("dashboards.addWidget", { name: label })}
        onClick={() => {
          add.reset();
          setOpen((o) => !o);
        }}
        className="rounded px-1.5 py-0.5 text-brand underline-offset-2 hover:underline"
      >
        {t("dashboards.addToDashboard")}
      </button>
      {add.isSuccess && (
        <p role="status" className="px-1.5 text-gray-800">
          <Link
            to="/dashboards/$dashboardId"
            params={{ dashboardId: add.data.id }}
            className="text-brand underline-offset-2 hover:underline"
          >
            {t("dashboards.added", { name: add.data.name })}
          </Link>
        </p>
      )}
      {open && (
        <ul id={listId} className="mt-1 space-y-0.5 rounded-md border border-gray-200 bg-white p-1">
          {editable.map((d) => (
            <li key={d.id}>
              <button
                type="button"
                disabled={add.isPending}
                onClick={() => add.mutate({ id: d.id })}
                className="w-full rounded px-2 py-1 text-left text-gray-900 hover:bg-gray-100"
              >
                {d.name}
              </button>
            </li>
          ))}
          <li>
            <button
              type="button"
              disabled={add.isPending}
              onClick={() => add.mutate("new")}
              className="w-full rounded px-2 py-1 text-left font-medium text-brand hover:bg-gray-100"
            >
              {t("dashboards.newWith", { name: deviceName })}
            </button>
          </li>
        </ul>
      )}
      {add.isError && (
        <p role="alert" className="px-1.5 text-red-900">
          {add.error instanceof Error && add.error.message === "full"
            ? t("dashboards.full", { max: MAX_WIDGETS })
            : saveErrors(add.error, t, () => label).join(" ")}
        </p>
      )}
    </div>
  );
}
